import React, {
    useEffect,
    useRef,
    useState,
    forwardRef,
    useImperativeHandle,
    useMemo,
} from 'react'
import { useToggle, useGetState } from 'ahooks'
import { throttle } from 'lodash'
import checkIcon from './img/check.svg'
import arrowIcon from './img/arrow.svg'
import closeIcon from './img/close.svg'
import refreshIcon from './img/refresh.svg'
import loadingIcon from './img/loading.svg'
import errorIcon from './img/error.svg'

export interface CaptchaProps {
    getCaptcha: (params: any) => Promise<{ id: string; imgName: string }>
    onSubmit: (params: { id: string; distance: number }) => Promise<void>
    onCancel?: () => Promise<void>
}

export interface CaptchaRef {
    open: (params: any) => void
}

export const Captcha = forwardRef<CaptchaRef, CaptchaProps>((props, ref) => {
    const [status, setStatus] = useState<'loading' | 'success' | 'error'>(
        'loading',
    )
    const [isModalOpen, { toggle }] = useToggle(false)
    const close = async () => {
        if (props?.onCancel) {
            await props?.onCancel()
        }
        toggle()
    }
    // https://file.gatherstar.io
    const captchaHost =
        'https://file.gatherstar.io'
    const captchaBg = captchaHost + '/front-resource/captcha/1.0/backgrounds/'
    const captchaIcon = captchaHost + '/front-resource/captcha/1.0/sliders/'
    const [{ imgName, id }, setCaptcha, getCaptchaState] = useGetState({
        id: '',
        imgName: '',
    })
    // 留着后面做加密
    const [formData, setFormData] = useState<any>({})
    const formDataRef = useRef<any>({})

    const refresh = useMemo(
        () =>
            throttle(
                async () => {
                    try {
                        setStatus('loading')
                        const { id, imgName } = await props.getCaptcha(
                            formDataRef.current,
                        )
                        setCaptcha({ id, imgName })
                        setStatus('success')
                    } catch (error) {
                        setStatus('error')
                    }
                },
                2000,
                { leading: true, trailing: false },
            ),
        [],
    )

    const open = (params: any) => {
        setFormData(params)
        formDataRef.current = params
        toggle()
        refresh()
    }

    // 暴露open方法给父组件
    useImperativeHandle(ref, () => ({
        open,
    }))

    // 滑块的左边距，结果需加上
    const marginLeft = 4
    const [sliderX, setSliderX] = useState(0)
    const [isDragging, setIsDragging] = useState(false)
    const [isAnimating, setIsAnimating] = useState(false) // 控制动画
    const startXRef = useRef(0)
    const startPositionRef = useRef(0)

    const parentWidth = 390
    const sliderWidth = 50
    const maxSlideDistance = parentWidth - sliderWidth

    // 获取clientX（兼容鼠标和触摸）
    const getClientX = (e: MouseEvent | TouchEvent): number => {
        if ('touches' in e) {
            return e.touches[0]?.clientX || 0
        }
        return e.clientX
    }

    // 开始拖动
    const handleStart = (e: React.MouseEvent | React.TouchEvent) => {
        e.preventDefault()
        setIsDragging(true)
        setIsAnimating(false) // 拖动时禁用动画
        startXRef.current = getClientX(e.nativeEvent as MouseEvent | TouchEvent)
        startPositionRef.current = sliderX
    }

    // 移动中
    const handleMove = (e: MouseEvent | TouchEvent) => {
        if (!isDragging) return

        const currentX = getClientX(e)
        const deltaX = currentX - startXRef.current
        const newX = startPositionRef.current + deltaX

        // 限制范围
        const boundedX = Math.max(0, Math.min(newX, maxSlideDistance))
        setSliderX(boundedX)
    }

    // 结束拖动
    const handleEnd = async () => {
        const preImg = getCaptchaState().imgName
        setIsDragging(false)
        try {
            setStatus('loading')
            await props.onSubmit({ id, distance: sliderX + marginLeft })
            setTimeout(() => {
                setStatus('success')
            }, 500)
        } catch (error) {
            // 验证码错误
            if (Number(error) === 4013) {
                setStatus('error')
                setTimeout(() => {
                    // 若用户没有手动刷新，则自动刷新
                    if (preImg === getCaptchaState().imgName) {
                        refresh()
                    }
                }, 3000)
            } else {
                // 账密错误
                toggle()
            }
        }
        setIsAnimating(true)
        setSliderX(0)
    }

    // 添加全局事件监听
    useEffect(() => {
        if (isDragging) {
            window.addEventListener('mousemove', handleMove)
            window.addEventListener('mouseup', handleEnd)
            window.addEventListener('touchmove', handleMove, { passive: false })
            window.addEventListener('touchend', handleEnd)
        }

        return () => {
            window.removeEventListener('mousemove', handleMove)
            window.removeEventListener('mouseup', handleEnd)
            window.removeEventListener('touchmove', handleMove)
            window.removeEventListener('touchend', handleEnd)
        }
    }, [isDragging, sliderX])
    return (
        <div
            className={`w-screen h-screen fixed z-[2] top-0 left-0 bg-[lightgray]/50 ${
                isModalOpen ? 'visible' : 'hidden'
            }`}
        >
            <div className="w-full h-full flex justify-center items-center">
                <div className="w-[448px] flex flex-col items-center rounded-[16px] border border-solid border-[#EEE] bg-white shadow-[0_16px_32px_-12px_rgba(14,19,26,0.1)]">
                    <div className="w-full h-[64px] flex justify-between items-center border-0 border-b-[1px] border-solid border-[#EEE]">
                        <div className="flex items-center ml-[24px]">
                            <img
                                src={checkIcon}
                                className="w-[20px] h-[20px]"
                            ></img>
                            <div className="text-[16px] font-medium ml-[12px] text-[#111]">
                                请完成以下验证
                            </div>
                        </div>
                        <img
                            src={closeIcon}
                            className="w-[16px] h-[16px] mr-[24px] cursor-pointer"
                            onClick={close}
                        ></img>
                    </div>
                    <div className="w-[400px] py-[24px] flex flex-col">
                        <div className="w-full h-[250px] relative rounded-[8px] overflow-hidden">
                            <img
                                src={captchaBg + imgName}
                                className={`w-full h-full object-cover ${
                                    imgName ? 'opacity-100' : 'opacity-0'
                                }`}
                            ></img>
                            <div
                                onMouseDown={handleStart}
                                onTouchStart={handleStart}
                                style={{
                                    transform: `translateX(${sliderX}px)`,
                                    transition: isAnimating
                                        ? 'transform 0.3s ease-out'
                                        : 'none',
                                }}
                                className={`w-[50px] h-[50px] absolute top-[100px] left-[4px] cursor-grab rounded-[4px] overflow-hidden border border-solid border-[#F5F5F5] shadow-[0_1px_2px_0_rgba(14,19,26,0.32)] ${
                                    imgName ? 'opacity-100' : 'opacity-0'
                                }`}
                            >
                                <img
                                    src={captchaIcon + imgName}
                                    className="w-full h-full object-cover pointer-events-none select-none"
                                />
                            </div>
                            <div
                                className={`w-[400px] h-[250px] absolute top-0 left-0 z-[9] rounded-[4px] overflow-hidden bg-black/40 backdrop-blur-[4px] ${
                                    status === 'success'
                                        ? 'opacity-0'
                                        : 'opacity-100'
                                }`}
                            >
                                <div className="w-full h-full flex flex-col justify-center items-center text-white text-[16px] font-medium leading-[24px]">
                                    {status === 'error' ? (
                                        <img
                                            src={errorIcon}
                                            className="w-[48px] h-[48px] mb-[8px]"
                                        ></img>
                                    ) : (
                                        <img
                                            src={loadingIcon}
                                            className="w-[48px] h-[48px] mb-[8px] animate-spin"
                                        ></img>
                                    )}
                                    {status === 'error' ? (
                                        <>
                                            <span>验证失败</span>
                                            <span>请刷新重试</span>
                                        </>
                                    ) : (
                                        <span>加载中...</span>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="w-full h-[48px] p-[4px] border border-[#eee] bg-[#F5F5F5] rounded-[8px] my-[16px]">
                            <div
                                onMouseDown={handleStart}
                                onTouchStart={handleStart}
                                style={{
                                    transform: `translateX(${sliderX}px)`,
                                    transition: isAnimating
                                        ? 'transform 0.3s ease-out'
                                        : 'none',
                                }}
                                className={`w-[50px] h-full flex justify-center items-center rounded-[4px] bg-white shadow-[0_6px_10px_0_rgba(14,19,26,0.06),0_2px_4px_0_rgba(14,19,26,0.03)] cursor-grab ${
                                    status === 'error'
                                        ? 'pointer-events-none'
                                        : ''
                                }`}
                            >
                                <img
                                    src={arrowIcon}
                                    className="w-[24px] h-[24px] pointer-events-none select-none"
                                ></img>
                            </div>
                        </div>
                        <div className="flex justify-between items-center">
                            <div className="text-[#111] text-[14px]">
                                按住上方模块向右滑动完成拼图
                            </div>
                            <div
                                className="flex items-center cursor-pointer"
                                onClick={refresh}
                            >
                                <img
                                    src={refreshIcon}
                                    className="w-[14px] h-[14px]"
                                ></img>
                                <div className="text-[#5D5D5D] text-[14px] ml-[4px]">
                                    刷新
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
})
